import { createReducer } from 'store/createReducer';
import {
  HEADER_SEARCH_AUTO_DATA_LOADED,
  HEADER_SEARCH_AUTO_DATA_LOADING,
  HEADER_SEARCH_CLOSE,
  HEADER_SEARCH_OPEN,
  SearchAction
} from './actions';
import { AutoCompleteModel } from 'models/AutoCompleteModel';
import { LoadingStatus } from 'models/LoadingStatus';
import { UPDATE_CURRENT_PAGE } from 'components/Pagelayout/actions';
import { HEADER_SEARCH_KEYDOWN, SearchKeyDownAction } from './themes/default/actions';
import { Action } from 'redux';
import { ISearchItem } from 'models/ISearchItem';
import { SuggestedCategory } from 'models/SuggestedCategory';
import { hasElements } from 'utils';

import { AutoCompleteSuggestion } from 'models/AutoCompleteSuggestion';

const maxDownStep = (suggestion: AutoCompleteSuggestion) => {
  if (suggestion.suggestedBrandCategories.length === 0 && suggestion.selectedColumnIndex === 1) {
    return suggestion.suggestedCategories.length;
  }
  return suggestion.suggestedProducts.length;
};

const numberOfColumnsInSearch = (suggestion: AutoCompleteSuggestion) => {
  if (suggestion.suggestedBrandCategories.length > 0 || suggestion.suggestedCategories.length > 0) {
    return 2;
  }

  return 1;
};

export const HEADER_SEARCH_SELECT_ITEM = 'HEADER_SEARCH_SELECT_ITEM';
export interface SelectSearchItemAction extends Action {
  id?: string;
}

const makeResultMapper =
  (id?: string) =>
  <T extends ISearchItem>(results: T[]) =>
    results && results.map<T>(selectItem(id));

export const selectSearchItem = (id?: string): SelectSearchItemAction => ({
  type: HEADER_SEARCH_SELECT_ITEM,
  id
});

const isCategory = (item: ISearchItem): item is SuggestedCategory =>
  (item as SuggestedCategory).categories !== undefined;
const selectItem =
  <T extends ISearchItem>(id?: string) =>
  (item: T) => {
    const _item = item.id === id ? { ...item, selected: true } : { ...item, selected: false };

    if (isCategory(_item) && hasElements(_item.categories)) {
      _item.categories = _item.categories.map(selectItem(id));
    }
    return _item;
  };

const searchReducer = {
  // default
  [HEADER_SEARCH_AUTO_DATA_LOADED]: (state: AutoCompleteModel, action: SearchAction) => {
    return {
      ...state,
      suggestion: action.suggestion || initialState.suggestion,
      loadingStatus: LoadingStatus.Completed
    };
  },
  [HEADER_SEARCH_AUTO_DATA_LOADING]: (state: AutoCompleteModel) => {
    return {
      ...state,
      loadingStatus: LoadingStatus.Fetching
    };
  },
  [HEADER_SEARCH_CLOSE]: (state: AutoCompleteModel) => {
    return {
      ...state,
      showResult: false
    };
  },
  [HEADER_SEARCH_OPEN]: (state: AutoCompleteModel) => {
    return {
      ...state,
      showResult: true
    };
  },
  [HEADER_SEARCH_KEYDOWN]: (state: AutoCompleteModel, action: SearchKeyDownAction) => {
    // ArrowLeft, ArrowRight, ArrowUp, ArrowDown
    if (state.suggestion !== undefined) {
      if (action.key === 'ArrowRight') {
        if (state.suggestion.selectedItemIndex === -1) {
          return { ...state };
        }
        let columnIndex = state.suggestion.selectedColumnIndex + 1;
        if (columnIndex > numberOfColumnsInSearch(state.suggestion) - 1) {
          columnIndex = columnIndex - 1;
        }

        return {
          ...state,
          suggestion: {
            ...state.suggestion,
            selectedColumnIndex: columnIndex
          }
        };
      } else if (action.key === 'ArrowLeft') {
        if (state.suggestion.selectedItemIndex === -1) {
          return { ...state };
        }
        let columnIndex = state.suggestion.selectedColumnIndex - 1;
        if (columnIndex < 0) {
          columnIndex = 0;
        }

        return {
          ...state,
          suggestion: {
            ...state.suggestion,
            selectedColumnIndex: columnIndex
          }
        };
      } else if (action.key === 'ArrowUp') {
        let index = state.suggestion.selectedItemIndex - 1;
        if (index < 0) {
          index = 0;
        }

        return {
          ...state,
          suggestion: {
            ...state.suggestion,
            selectedItemIndex: index
          }
        };
      } else if (action.key === 'ArrowDown') {
        let index = state.suggestion.selectedItemIndex + 1;

        if (
          state.suggestion.suggestedBrandCategories.length > 0 &&
          state.suggestion.selectedColumnIndex === 1
        ) {
          index = 4;
        }

        if (index > maxDownStep(state.suggestion) - 1) {
          index = maxDownStep(state.suggestion) - 1;
        }

        return {
          ...state,
          suggestion: {
            ...state.suggestion,
            selectedItemIndex: index
          }
        };
      }
    }

    return state;
  },
  [UPDATE_CURRENT_PAGE]: (state: AutoCompleteModel): AutoCompleteModel => {
    const mapResults = makeResultMapper();
    return {
      ...state,
      suggestion: {
        latestSearches: mapResults(state.suggestion.latestSearches),
        popularSearchPhrases: mapResults(state.suggestion.popularSearchPhrases),
        searchString: '',
        suggestedProducts: [],
        suggestedPhrases: [],
        suggestedBrandCategories: [],
        suggestedDidYouMean: [],
        suggestedCategories: [],
        selectedColumnIndex: 0,
        selectedItemIndex: -1
      },
      showResult: false,
      searchString: ''
    };
  },
  [HEADER_SEARCH_SELECT_ITEM]: (state: AutoCompleteModel, { id }: SelectSearchItemAction) => {
    const mapResults = makeResultMapper(id);
    return {
      ...state,
      suggestion: {
        ...state.suggestion,
        suggestedBrandCategories: mapResults(state.suggestion.suggestedBrandCategories),
        suggestedCategories: mapResults(state.suggestion.suggestedCategories),
        suggestedPhrases: mapResults(state.suggestion.suggestedPhrases),
        suggestedProducts: mapResults(state.suggestion.suggestedProducts),
        suggestedDidYouMean: mapResults(state.suggestion.suggestedDidYouMean),
        popularSearchPhrases: mapResults(state.suggestion.popularSearchPhrases),
        latestSearches: mapResults(state.suggestion.latestSearches)
      }
    };
  }
};

export const initialState: AutoCompleteModel = {
  suggestion: {
    suggestedProducts: [],
    latestSearches: [],
    popularSearchPhrases: [],
    searchString: '',
    selectedColumnIndex: -1,
    selectedItemIndex: -1,
    suggestedBrandCategories: [],
    suggestedCategories: [],
    suggestedDidYouMean: [],
    suggestedPhrases: []
  },
  showResult: false,
  searchUrl: '',
  searchString: '',
  loadingStatus: LoadingStatus.Idle
};

export const search = createReducer(initialState, searchReducer);
